// Port of SuiteModel/StoryModel.swift's inference and the index's presence projection (SearchIndex.projection):
// presence is explicit (the fragment's `presence` map) or inferred — the POV field makes a character POV, a name or alias
// in the text makes an entity mentioned (whole words; characters and places case-sensitively, objects and motifs not).
// Mentions aren't marked up in the prose, so this reads the stored text as it is. Shared by the Story room (timeline,
// continuity, scene facts) and the Plan room's collections (the `entity` rule).
import type { EntityKind, FragmentKind, MetadataValue } from './contract.ts';
import { speakers as fountainSpeakers } from './fountain.ts';
import type { OpenedProject } from './project.ts';
import type { StoryEntity } from './model/documents.ts';
import { scalarOffsetOfUTF16 } from './automerge/offsets.ts';
import { graphemes, trimCFWhitespace } from './unicode.ts';
import { escapeRegExp } from './text/icuRegex.ts';

export type PresenceLevel = 'absent' | 'mentioned' | 'on-stage' | 'pov';
export const PRESENCE_LEVELS: readonly PresenceLevel[] = ['absent', 'mentioned', 'on-stage', 'pov'];
export const PRESENCE_LABELS: Record<PresenceLevel, string> = { absent: 'Absent', mentioned: 'Mentioned', 'on-stage': 'On stage', pov: 'POV' };
/** `PresenceLevel <`: weakest to strongest. */
export const presenceRank = (l: PresenceLevel) => PRESENCE_LEVELS.indexOf(l);

export const ENTITY_KIND_LABELS: Record<EntityKind, string> = { character: 'Character', place: 'Place', object: 'Object', motif: 'Motif' };

export interface Presence {
  entityID: string;
  level: PresenceLevel;
  /** Worked out from the text or the POV field, not set by the writer. */
  inferred: boolean;
}

/** A presence row as the index keeps it (`PresenceRow`). */
export interface PresenceRow extends Presence { fragmentID: string }

/** `StoryEntity.honorifics`: articles and titles a short name skips. */
const HONORIFICS = new Set(['the', 'a', 'an', 'mr', 'mr.', 'mrs', 'mrs.', 'ms', 'ms.', 'miss', 'mx', 'mx.', 'dr', 'dr.',
  'sir', 'dame', 'lady', 'lord', 'inspector', 'sergeant', 'captain', 'professor', 'prof.']);

/** `StoryEntity.shortName`: a character in a word or two, as a card's foot names them — their first alias ("Utterson"
 *  for Gabriel John Utterson), else the name's first word after any article or title ("Mr. Guest" → "Guest", "The
 *  maid-servant" → "Maid-servant"). */
export function shortName(entity: Pick<StoryEntity, 'name' | 'aliases'>): string {
  // `trimmingCharacters(in: .whitespaces)`: spaces and tabs.
  const alias = entity.aliases.map((a) => a.replace(/^[\t\p{Zs}]+|[\t\p{Zs}]+$/gu, '')).find((a) => a !== '');
  if (alias !== undefined) return alias;
  // `split(separator: " ")`: empty pieces left out.
  const words = entity.name.split(' ').filter((w) => w !== '');
  let skipped = 0;
  while (skipped < words.length && HONORIFICS.has(words[skipped].toLowerCase())) skipped += 1;
  const first = words[skipped];
  if (first === undefined) return words[0] ?? entity.name;
  if (skipped === 0) return first;
  const [head = '', ...tail] = graphemes(first);
  return head.toUpperCase() + tail.join('');
}

/** The subset of `StoryEntity` the inference reads. */
export type EntityNames = Pick<StoryEntity, 'id' | 'kind' | 'name' | 'aliases'>;

/** `StoryEntity.names`: every name the text may use for this entity (trimmed, at least two characters). */
export function entityNames(e: Pick<StoryEntity, 'name' | 'aliases'>): string[] {
  return [e.name, ...e.aliases].map(trimCFWhitespace).filter((n) => graphemes(n).length >= 2);
}

/** Characters and places are proper nouns; objects and motifs ("the lamp", "rain") are not. */
const caseSensitive = (kind: EntityKind) => kind === 'character' || kind === 'place';

const patterns = new Map<string, RegExp>();
/** `StoryModel.pattern`: a whole-word pattern for a name (cached). */
export function namePattern(name: string, sensitive: boolean): RegExp {
  const key = (sensitive ? 'c:' : 'i:') + name;
  let re = patterns.get(key);
  if (!re) {
    if (patterns.size > 5000) patterns.clear();
    re = new RegExp(`(?<![\\p{L}\\p{N}])${escapeRegExp(name)}(?![\\p{L}\\p{N}])`, sensitive ? 'gu' : 'giu');
    patterns.set(key, re);
  }
  re.lastIndex = 0;
  return re;
}

export interface Mention {
  entityID: string;
  /** Unicode-scalar [start, end) in the text. */
  range: [number, number];
}

/** `StoryModel.mentions(in:entities:)`: every place the text names an entity, in text order. */
export function mentions(text: string, entities: readonly EntityNames[]): Mention[] {
  const found: { entityID: string; start: number; end: number }[] = [];
  for (const entity of entities) {
    for (const name of entityNames(entity)) {
      for (const m of text.matchAll(namePattern(name, caseSensitive(entity.kind)))) {
        found.push({ entityID: entity.id, start: m.index!, end: m.index! + m[0].length });
      }
    }
  }
  found.sort((a, b) => a.start - b.start);
  return found.map((f) => ({ entityID: f.entityID, range: [scalarOffsetOfUTF16(text, f.start), scalarOffsetOfUTF16(text, f.end)] }));
}

/** `StoryModel.mentionedIDs`: the entities the text names anywhere. */
export function mentionedIDs(text: string, entities: readonly EntityNames[]): Set<string> {
  const out = new Set<string>();
  for (const entity of entities) {
    if (entityNames(entity).some((n) => namePattern(n, caseSensitive(entity.kind)).test(text))) out.add(entity.id);
  }
  return out;
}

/** `StoryModel.speakingIDs`: the characters a script's cues name (by name, short name or alias, case aside). */
export function speakingIDs(text: string, entities: readonly EntityNames[]): Set<string> {
  const speakers = fountainSpeakers(text);
  const out = new Set<string>();
  if (!speakers.size) return out;
  for (const e of entities) {
    if (e.kind !== 'character') continue;
    if ([...entityNames(e), shortName(e)].some((n) => speakers.has(n.toUpperCase()))) out.add(e.id);
  }
  return out;
}

/** `FragmentDocument.explicitPresence()`: the writer's settings, readable levels only. */
export function explicitPresence(stored: Record<string, string>): Map<string, PresenceLevel> {
  const out = new Map<string, PresenceLevel>();
  for (const [id, raw] of Object.entries(stored)) if ((PRESENCE_LEVELS as readonly string[]).includes(raw)) out.set(id, raw as PresenceLevel);
  return out;
}

/** `StoryModel.presence`: explicit settings win; otherwise the POV makes a character POV, a script's cue (`kind`
 *  'screenplay') a character who speaks on stage, and a mention mentioned. Entities in the order given; absent ones left out. */
export function presence(text: string, pov: string | null, explicit: Map<string, PresenceLevel>, entities: readonly EntityNames[],
  kind: FragmentKind = 'prose'): Presence[] {
  const mentioned = mentionedIDs(text, entities.filter((e) => !explicit.has(e.id) && e.id !== pov));
  const speaking = kind === 'screenplay' ? speakingIDs(text, entities) : new Set<string>();
  const out: Presence[] = [];
  for (const entity of entities) {
    const level = explicit.get(entity.id);
    if (level !== undefined) {
      if (level !== 'absent') out.push({ entityID: entity.id, level, inferred: false });
    } else if (entity.id === pov) {
      out.push({ entityID: entity.id, level: 'pov', inferred: true });
    } else if (speaking.has(entity.id)) {
      out.push({ entityID: entity.id, level: 'on-stage', inferred: true });
    } else if (mentioned.has(entity.id)) {
      out.push({ entityID: entity.id, level: 'mentioned', inferred: true });
    }
  }
  return out;
}

/** An entity-reference field's value (`if case let .text(t)?, !t.isEmpty`). */
export function reference(metadata: Record<string, MetadataValue>, field: string): string | null {
  const v = metadata[field];
  return v?.type === 'text' && v.value !== '' ? v.value : null;
}

/** The index's presence rows for one fragment, as `SearchIndex.projection` makes them. */
export function fragmentPresence(project: OpenedProject, fragmentID: string): Presence[] {
  const f = project.fragmentRecord(fragmentID);
  if (!f) return [];
  return presence(f.text, reference(f.metadata, 'pov'), explicitPresence(f.presence), project.record.entities, f.kind);
}

/** Every fragment's presence (the index's `presence` table for the project), in fragment-id order. */
export function presenceRows(project: OpenedProject): PresenceRow[] {
  const rows: PresenceRow[] = [];
  for (const id of project.fragmentIds().sort()) for (const p of fragmentPresence(project, id)) rows.push({ fragmentID: id, ...p });
  return rows;
}
